
import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';

interface Review {
  id: string;
  order_id: string;
  customer_email: string;
  customer_name: string;
  rating: number;
  comment: string;
  created_at: string;
  order_title?: string;
}

interface ReviewsPageProps {
  customerEmail: string;
  onClose: () => void;
}

export default function ReviewsPage({ customerEmail, onClose }: ReviewsPageProps) {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [completedOrders, setCompletedOrders] = useState<{ id: string; order_title: string }[]>([]);
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [reviewedOrderIds, setReviewedOrderIds] = useState<string[]>([]);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (data) {
        const orderIds = [...new Set(data.map(r => r.order_id))];
        let orderTitles: Record<string, string> = {};

        if (orderIds.length > 0) {
          const { data: ordersData } = await supabase
            .from('orders')
            .select('id, order_title')
            .in('id', orderIds);

          if (ordersData) {
            ordersData.forEach(o => {
              orderTitles[o.id] = o.order_title;
            });
          }
        }

        const enrichedReviews = data.map(r => ({
          ...r,
          order_title: orderTitles[r.order_id] || 'Sipariş',
        }));

        setReviews(enrichedReviews);
        setReviewedOrderIds(data.map(r => r.order_id));
      }
    } catch (error) {
      console.error('Değerlendirmeler yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompletedOrders = async () => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('id, order_title')
        .eq('customer_email', customerEmail)
        .in('status', ['shipped', 'sent_to_print']);

      if (error) throw error;

      if (data) {
        const available = data.filter(o => !reviewedOrderIds.includes(o.id));
        setCompletedOrders(available);
      }
    } catch (error) {
      console.error('Siparişler yüklenirken hata:', error);
    }
  };

  const handleOpenAddModal = () => {
    fetchCompletedOrders();
    setShowAddModal(true);
    setRating(0);
    setComment('');
    setSelectedOrderId('');
    setSubmitError('');
    setSubmitSuccess(false);
  };

  const handleSubmitReview = async () => {
    if (!selectedOrderId) {
      setSubmitError('Lütfen bir sipariş seçin');
      return;
    }
    if (rating === 0) {
      setSubmitError('Lütfen bir puan verin');
      return;
    }
    if (!comment.trim()) {
      setSubmitError('Lütfen bir yorum yazın');
      return;
    }

    setSubmitting(true);
    setSubmitError('');

    try {
      const { data: userData } = await supabase.auth.getUser();
      const userName = userData?.user?.user_metadata?.name || customerEmail.split('@')[0];

      const { error } = await supabase
        .from('reviews')
        .insert({
          order_id: selectedOrderId,
          customer_email: customerEmail,
          customer_name: userName,
          rating,
          comment: comment.trim(),
        });

      if (error) throw error;

      setSubmitSuccess(true);
      setTimeout(() => {
        setShowAddModal(false);
        fetchReviews();
      }, 1500);
    } catch (error: any) {
      console.error('Değerlendirme gönderilirken hata:', error);
      setSubmitError('Değerlendirme gönderilirken bir hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffDays < 1) return 'Bugün';
    if (diffDays === 1) return 'Dün';
    if (diffDays < 7) return `${diffDays} gün önce`;

    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const maskEmail = (email: string) => {
    const [local, domain] = email.split('@');
    if (local.length <= 2) return `${local[0]}***@${domain}`;
    return `${local.slice(0, 2)}***@${domain}`;
  };

  const averageRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : '0.0';

  const ratingDistribution = [5, 4, 3, 2, 1].map(star => ({
    star,
    count: reviews.filter(r => r.rating === star).length,
    percentage: reviews.length > 0
      ? (reviews.filter(r => r.rating === star).length / reviews.length) * 100
      : 0,
  }));

  const renderStars = (count: number, size: string = 'text-base') => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map(star => (
          <i
            key={star}
            className={`${star <= count ? 'ri-star-fill text-amber-400' : 'ri-star-line text-gray-300'} ${size}`}
          ></i>
        ))}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-5 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <h2 className="text-gray-900 font-semibold text-lg flex items-center gap-2">
            <i className="ri-star-smile-line text-amber-500"></i>
            Değerlendirmeler
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={handleOpenAddModal}
              className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white text-sm font-medium rounded-lg transition-all cursor-pointer flex items-center gap-2 whitespace-nowrap shadow-sm"
            >
              <i className="ri-edit-line"></i>
              Değerlendir
            </button>
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <i className="ri-loader-4-line text-3xl text-green-500 animate-spin"></i>
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-16 px-4">
              <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-star-line text-3xl text-amber-400"></i>
              </div>
              <p className="text-gray-900 font-medium mb-1">Henüz değerlendirme yok</p>
              <p className="text-gray-500 text-sm mb-5">İlk değerlendirmeyi siz yapın!</p>
              <button
                onClick={handleOpenAddModal}
                className="px-5 py-2.5 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap shadow-sm"
              >
                Değerlendirme Yap
              </button>
            </div>
          ) : (
            <>
              {/* Özet Bölümü */}
              <div className="p-5 border-b border-gray-100 bg-gray-50/50">
                <div className="flex flex-col sm:flex-row items-center gap-5">
                  {/* Ortalama Puan */}
                  <div className="text-center flex-shrink-0">
                    <div className="text-4xl font-bold text-gray-900 mb-1">{averageRating}</div>
                    {renderStars(Math.round(Number(averageRating)), 'text-lg')}
                    <p className="text-gray-500 text-xs mt-1.5">{reviews.length} değerlendirme</p>
                  </div>

                  {/* Dağılım */}
                  <div className="flex-1 w-full space-y-1.5">
                    {ratingDistribution.map(item => (
                      <div key={item.star} className="flex items-center gap-2">
                        <span className="text-xs text-gray-600 w-4 text-right">{item.star}</span>
                        <i className="ri-star-fill text-amber-400 text-xs"></i>
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-amber-400 rounded-full transition-all duration-500"
                            style={{ width: `${item.percentage}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-gray-500 w-6 text-right">{item.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Yorumlar Listesi */}
              <div className="divide-y divide-gray-100">
                {reviews.map((review) => (
                  <div key={review.id} className="p-4 lg:p-5">
                    <div className="flex items-start gap-3">
                      {/* Avatar */}
                      <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-semibold text-xs">
                          {getInitials(review.customer_name)}
                        </span>
                      </div>

                      <div className="flex-1 min-w-0">
                        {/* Üst Bilgi */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 mb-1.5">
                          <div>
                            <span className="text-gray-900 font-medium text-sm">{review.customer_name}</span>
                            <span className="text-gray-400 text-xs ml-2">{maskEmail(review.customer_email)}</span>
                          </div>
                          <span className="text-gray-400 text-xs">{formatDate(review.created_at)}</span>
                        </div>

                        {/* Yıldızlar */}
                        <div className="mb-2">
                          {renderStars(review.rating, 'text-sm')}
                        </div>

                        {/* Sipariş Bilgisi */}
                        {review.order_title && (
                          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 rounded-md mb-2">
                            <i className="ri-shopping-bag-line text-gray-500 text-xs"></i>
                            <span className="text-gray-600 text-xs">{review.order_title}</span>
                          </div>
                        )}

                        {/* Yorum */}
                        <p className="text-gray-700 text-sm leading-relaxed">{review.comment}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Değerlendirme Ekleme Modalı */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-gray-900 font-semibold text-lg flex items-center gap-2">
                <i className="ri-star-smile-line text-amber-500"></i>
                Değerlendirme Yap
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {submitSuccess ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-checkbox-circle-line text-3xl text-green-600"></i>
                </div>
                <p className="text-gray-900 font-medium text-lg mb-1">Teşekkürler!</p>
                <p className="text-gray-500 text-sm">Değerlendirmeniz başarıyla gönderildi.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Sipariş Seçimi */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Sipariş Seçin <span className="text-red-500">*</span>
                  </label>
                  {completedOrders.length === 0 ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700 flex items-center gap-2">
                      <i className="ri-information-line flex-shrink-0"></i>
                      <span>Değerlendirilebilecek tamamlanmış siparişiniz bulunmuyor.</span>
                    </div>
                  ) : (
                    <select
                      value={selectedOrderId}
                      onChange={(e) => setSelectedOrderId(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none bg-white cursor-pointer"
                    >
                      <option value="">Sipariş seçin...</option>
                      {completedOrders.map(order => (
                        <option key={order.id} value={order.id}>{order.order_title}</option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Yıldız Puanlama */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Puanınız <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map(star => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setRating(star)}
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                        className="p-1 cursor-pointer transition-transform hover:scale-110"
                      >
                        <i className={`text-2xl ${
                          star <= (hoverRating || rating)
                            ? 'ri-star-fill text-amber-400'
                            : 'ri-star-line text-gray-300'
                        }`}></i>
                      </button>
                    ))}
                    {rating > 0 && (
                      <span className="text-sm text-gray-500 ml-2">
                        {rating === 1 ? 'Kötü' : rating === 2 ? 'Fena Değil' : rating === 3 ? 'İyi' : rating === 4 ? 'Çok İyi' : 'Mükemmel'}
                      </span>
                    )}
                  </div>
                </div>

                {/* Yorum */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Yorumunuz <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    rows={4}
                    maxLength={500}
                    placeholder="Deneyiminizi paylaşın..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none resize-none"
                  />
                  <p className="text-gray-400 text-xs mt-1 text-right">{comment.length}/500</p>
                </div>

                {/* Hata Mesajı */}
                {submitError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600 flex items-center gap-2">
                    <i className="ri-error-warning-line flex-shrink-0"></i>
                    <span>{submitError}</span>
                  </div>
                )}

                {/* Butonlar */}
                <div className="flex gap-3 pt-1">
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors cursor-pointer text-sm whitespace-nowrap"
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleSubmitReview}
                    disabled={submitting || completedOrders.length === 0}
                    className="flex-1 px-4 py-2.5 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-medium rounded-lg transition-all cursor-pointer text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    {submitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Gönderiliyor...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line"></i>
                        Gönder
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
